import { useOn } from "@/lib/features";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2, Hourglass, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useContent } from "@/lib/content";
import { useServices } from "@/lib/useServices";
import { Reveal } from "./Reveal";
import { SlotPicker, StaffPicker, TypePicker } from "./BookingParts";
import { PayStep, holdLabel } from "./PayStep";
import { AgreeNote } from "@/pages/Policy";
import { FreshaBookPanel, useFresha } from "@/lib/fresha";

const EMPTY = { name: "", email: "", phone: "", treatment: "", preferred_date: "", time_slot: "", notes: "", booking_type: "confirmed", staff_id: "any" };

const Field = ({ id, label, children }) => (
  <div className="field"><label htmlFor={id}>{label}</label>{children}</div>
);

const DONE = {
  confirmed: ["You're confirmed!", "Your deposit has been received and your time is reserved."],
  needs_review: ["Deposit received", "Your deposit was received. The team will confirm your time shortly."],
  tentative: ["Your time is held", "We've emailed you a link to pay your 50% deposit. If it isn't received within 24 hours, the slot may become available to another client who pays."],
  awaiting_payment: ["Your time is held", "We've emailed you a link to pay your 50% deposit. If it isn't received within 24 hours, the slot may become available to another client who pays."],
};

export const Confirmation = ({ b, onReset }) => {
  const [title, text] = DONE[b.status] || DONE.tentative;
  const ok = b.status === "confirmed" || b.status === "needs_review";
  return (
    <div className="form confirm-box" data-testid="booking-confirmation">
      {ok ? <CheckCircle2 size={36} color="var(--gold)" /> : <Hourglass size={34} color="var(--pink)" />}
      <h3 data-testid="booking-confirmation-title">{title}</h3>
      <p>{text}</p>
      <p className="confirm-detail"><strong>{b.treatment}</strong><br />{b.preferred_date} at {b.time_label}{b.staff_name && <> with <span data-testid="booking-confirmation-staff">{b.staff_name}</span></>}
        {!ok && b.hold_until && <><br /><span data-testid="booking-hold-until">Held until {holdLabel(b.hold_until)}</span></>}</p>
      <p className="muted-sm">A confirmation email is on its way to <strong>{b.email}</strong>. Reply to it any time to reach the spa.</p>
      {onReset && <button type="button" className="btn light" onClick={onReset} data-testid="booking-new-request-button">Make another booking</button>}
    </div>
  );
};

const BookingForm = ({ preset, onCreated }) => {
  const [f, setF] = useState(EMPTY);
  const [slot, setSlot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const qc = useQueryClient();
  useEffect(() => { if (preset) { setF((p) => ({ ...p, treatment: preset.key, time_slot: "", staff_id: "any" })); setSlot(null); } }, [preset]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const pickTreatment = (e) => { setF({ ...f, treatment: e.target.value, time_slot: "", staff_id: "any" }); setSlot(null); };
  const chosen = slot?.staff?.find((s) => s.id === f.staff_id);
  const held = slot ? (chosen ? chosen.state === "held" : slot.status === "held") : false;
  const { data } = useServices();
  const picked = data?.categories.flatMap((c) => c.items).find((s) => s.key === f.treatment);
  const canDeposit = !!picked?.amount;
  const type = canDeposit && !held ? f.booking_type : "tentative";

  const submit = async (e) => {
    e.preventDefault();
    if (!f.time_slot) { setError("Please choose a time."); return; }
    setError("");
    setBusy(true);
    try {
      const { data: res } = await api.post("/bookings", { ...f, booking_type: type });
      qc.invalidateQueries({ queryKey: ["slots"] });
      onCreated(res.booking, res.payment);
      setF(EMPTY);
      setSlot(null);
    } catch (err) {
      setError(formatApiError(err));
      qc.invalidateQueries({ queryKey: ["slots"] });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="form" onSubmit={submit} data-testid="booking-form">
      <Field id="name" label="Your name"><input id="name" required autoComplete="name" placeholder="Full name" value={f.name} onChange={set("name")} data-testid="booking-name-input" /></Field>
      <div className="field-row">
        <Field id="email" label="Email"><input id="email" required type="email" inputMode="email" autoComplete="email" placeholder="For your confirmation" value={f.email} onChange={set("email")} data-testid="booking-email-input" /></Field>
        <Field id="phone" label="Phone number"><input id="phone" required type="tel" inputMode="tel" autoComplete="tel" placeholder="Best contact number" value={f.phone} onChange={set("phone")} data-testid="booking-phone-input" /></Field>
      </div>
      <Field id="treatment" label="Treatment">
        <select id="treatment" required value={f.treatment} onChange={pickTreatment} data-testid="booking-treatment-select">
          <option value="">{data ? "Choose a service" : "Loading services…"}</option>
          {data?.categories.map((c) => (
            <optgroup key={c.id} label={c.label}>
              {c.items.map((s) => <option key={s.key} value={s.key}>{`${s.name} · ${s.price}`}</option>)}
            </optgroup>
          ))}
          {data && <option value={data.not_sure}>{data.not_sure}</option>}
        </select>
        {picked && <p className="picked" data-testid="booking-selected-service">{picked.key} · {picked.duration} · {picked.price}</p>}
      </Field>
      <Field id="date" label="Date"><input id="date" type="date" required min={new Date().toLocaleDateString("en-CA")} value={f.preferred_date} onChange={(e) => { setF({ ...f, preferred_date: e.target.value, time_slot: "", staff_id: "any" }); setSlot(null); }} data-testid="booking-date-input" /></Field>
      <div className="field"><label>Time</label>
        <SlotPicker day={f.preferred_date} treatment={f.treatment} value={f.time_slot} onChange={(t, status, staff) => { setF({ ...f, time_slot: t, staff_id: "any" }); setSlot({ status, staff }); }} />
      </div>
      {slot?.staff && (
        <div className="field"><label>Who would you like?</label>
          <StaffPicker staff={slot.staff} value={f.staff_id} onChange={(id) => setF({ ...f, staff_id: id })} />
        </div>
      )}
      <div className="field"><label>Booking type</label>
        <TypePicker value={type} onChange={(v) => setF({ ...f, booking_type: v })} deposit={(picked?.amount || 0) * 0.5} canDeposit={canDeposit} held={held} />
      </div>
      <Field id="notes" label="Anything we should know?"><textarea id="notes" placeholder="Optional notes" value={f.notes} onChange={set("notes")} data-testid="booking-notes-input" /></Field>
      {error && <p className="form-error" data-testid="booking-error">{error}</p>}
      <button className="btn gold" type="submit" disabled={busy} data-testid="booking-submit-button">
        {busy ? <Loader2 size={16} className="spin" /> : <>{type === "confirmed" ? "Continue to deposit" : "Send tentative request"} <ArrowUpRight size={14} /></>}
      </button>
      <small>You'll get an email confirmation that you can reply to. Deposits are 50% of the treatment price.</small>
      <AgreeNote action="booking" />
    </form>
  );
};

const BookByPhone = ({ c }) => (
  <div className="form fresha-panel" data-testid="book-by-phone-panel">
    <h3>Book by phone or WhatsApp</h3>
    <p>Call or message us and we'll find the perfect time for your treatment.</p>
    <div className="req-actions">
      <a className="btn gold" href={`tel:${c.phone_call}`} data-testid="book-by-phone-call">Call {c.phone_call_label}</a>
      <a className="btn light" href={`https://wa.me/${c.phone_call.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" data-testid="book-by-phone-whatsapp">WhatsApp us</a>
    </div>
  </div>
);

export const Booking = ({ preset }) => {
  const c = useContent().data;
  const fresha = useFresha();
  const bookingsOn = useOn("bookings");
  const [state, setState] = useState(null);
  const panel = useRef(null);
  useEffect(() => { if (preset) setState(null); }, [preset]);
  useEffect(() => { if (state) panel.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, [state]);

  const created = (b, payment) => {
    if (b.status === "awaiting_payment") setState({ step: "pay", b, payment });
    else { setState({ step: "done", b }); toast.success("Your time is held for 24 hours"); }
  };
  const done = (b) => setState({ step: "done", b });
  const later = async () => {
    const { data } = await api.post("/bookings/make-tentative", { booking_id: state.b.id, token: state.b.public_token });
    toast.success("Your time is held for 24 hours");
    done(data.booking);
  };

  return (
    <section className="section highlight" id="book" data-testid="booking-section">
      <div className="wrap booking">
        <Reveal>
          <p className="eyebrow">{c.booking.eyebrow}</p>
          <h2>{c.booking.title}</h2>
          <p>{c.booking.text}</p>
          {!fresha && bookingsOn && (
            <div className="notice" data-testid="deposit-notice">
              <strong>{c.booking.deposit_title}</strong><br />{c.booking.deposit_text}
            </div>
          )}
          <p><strong>Prefer to speak to someone?</strong><br />
            <a href={`tel:${c.contact.phone_call}`} data-testid="booking-call-link-1">{c.contact.phone_call_label}</a> · <a href={`tel:${c.contact.phone_alt}`} data-testid="booking-call-link-2">{c.contact.phone_alt_label}</a>
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div ref={panel} className="book-panel" />
          {fresha && <FreshaBookPanel url={fresha.url} />}
          {!fresha && !bookingsOn && <BookByPhone c={c.contact} />}
          {!fresha && bookingsOn && !state && <BookingForm preset={preset} onCreated={created} />}
          {state?.step === "pay" && <PayStep booking={state.b} payment={state.payment} onPaid={done} onLater={later} />}
          {state?.step === "done" && <Confirmation b={state.b} onReset={() => setState(null)} />}
        </Reveal>
      </div>
    </section>
  );
};
